import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_MS = 30000; // 30 segundos

// Session ID por aba para diferenciar viewers reais
function getOrCreateSessionId() {
  try {
    const k = 'op_viewer_session_id';
    let s = sessionStorage.getItem(k);
    if (!s) {
      s = `s_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
      sessionStorage.setItem(k, s);
    }
    return s;
  } catch {
    return `s_${Math.random().toString(36).slice(2, 14)}`;
  }
}

/**
 * Badge "X pessoas estão vendo este produto agora"
 * - Combina viewers reais (heartbeat a cada 30s) com boost fictício do backend
 * - Produtos esgotados (stock=0) só mostram viewers reais
 * - Fitness recebe boost maior do backend (4-9), outros (1-5)
 *
 * Props:
 *  - productId: string (obrigatório)
 *  - className: estilo extra opcional
 */
export default function ProductViewersBadge({ productId, className = '' }) {
  const [count, setCount] = useState(null);
  const sessionIdRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!productId) return;
    sessionIdRef.current = getOrCreateSessionId();

    let cancelled = false;

    const heartbeat = async () => {
      try {
        const { data } = await axios.post(
          `${API}/products/${productId}/heartbeat`,
          { session_id: sessionIdRef.current },
          { withCredentials: true }
        );
        if (!cancelled && typeof data?.viewers === 'number') {
          setCount(data.viewers);
        }
      } catch {
        // silent fail — não polui UI se backend cair
      }
    };

    heartbeat();
    intervalRef.current = setInterval(heartbeat, HEARTBEAT_MS);

    // Pausa heartbeat quando a aba fica oculta (economia + correto)
    const onVis = () => {
      if (document.hidden) {
        if (intervalRef.current) clearInterval(intervalRef.current);
      } else {
        heartbeat();
        intervalRef.current = setInterval(heartbeat, HEARTBEAT_MS);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [productId]);

  // Não renderiza nada até ter resposta — evita flicker
  if (count === null || count <= 0) return null;

  return (
    <div
      className={`inline-flex items-center gap-2 bg-slate-50 border border-slate-200 text-slate-700 rounded-full px-3 py-1.5 text-xs font-medium ${className}`}
      data-testid="product-viewers-badge"
      aria-live="polite"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600" />
      </span>
      <Eye className="h-3.5 w-3.5 text-slate-500" />
      <span className="text-slate-700">
        <strong className="font-semibold text-slate-900 tabular-nums">{count}</strong> {count === 1 ? 'pessoa está' : 'pessoas estão'} vendo este produto agora
      </span>
    </div>
  );
}
